import { GenericTable } from "@shared/components/GenericTable";
import { StepHelpers } from "@shared/components/Stepper";
import { cn } from "@shared/lib/utils";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import { Switch } from "@shared/ui/switch";
import { format } from "date-fns";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  useBillData,
  useConsumerCreditNote,
  useConsumerDetails,
  useConsumerLatestBill,
  useMeterdata,
  useMeterReadingStatus,
  usePlanData,
  useUpdateConsumerSelection,
} from "../../hooks";

interface Step3ReadingsConsumptionProps {
  // Props automatically passed by Stepper
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}

interface MeterData {
  id: string;
  meterNumber: string;
  deviceNo: string;
  productCode: string;
  previousReading: number;
  previousReadingDate: string;
  previousReadingMeterStatusDisplay: string;
  currentReading: number;
  currentReadingDate: string;
  currentReadingMeterStatus: string;
}

const Step3ReadingsConsumption: React.FC<Step3ReadingsConsumptionProps> = ({
  stepHelpers,
  currentStepIndex = 2,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating,
}) => {
  const { id, action = "add" } = useParams();

  const [meterUpdates, setMeterUpdates] = useState<{ [meterId: string]: any }>(
    {}
  );
  const [enableOutstanding, setEnableOutstanding] = useState(false);
  const [enableLateFee, setEnableLateFee] = useState(false);
  const [includeCredit, setIncludeCredit] = useState(false);
  const dataLoadedRef = useRef(false);

  const CURRENCY = useCurrency();

  // Helper function to save step data using stepHelpers
  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;

      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };

        stepHelpers.setStepData(currentStepIndex, stepData);
        console.log("Step 3 data saved:", stepData);
      } catch (error) {
        console.error("Failed to save step 3 data:", error);
      }
    },
    [stepHelpers, currentStepIndex]
  );

  // Helper function to get step data using stepHelpers
  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step 3 data:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  // Helper function to get step 1 data (consumer selection)
  const getStep1Data = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(0); // Step 1 is at index 0
    } catch (error) {
      console.error("Failed to get step 1 data:", error);
      return null;
    }
  }, [stepHelpers]);

  // Helper function to get step 2 data (account overview)
  const getStep2Data = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(1); // Step 2 is at index 1
    } catch (error) {
      console.error("Failed to get step 2 data:", error);
      return null;
    }
  }, [stepHelpers]);

  // Get billDraftId from step 1 data
  const step1Data = getStep1Data();
  const step2Data = getStep2Data();
  const billId = step1Data?.billDraftId || id;

  const {
    data: billData,
    isLoading: isBillLoading,
    isError: isBillError,
  } = useBillData(billId);

  const { mutateAsync: updateConsumer, isPending: isSubmitting } =
    useUpdateConsumerSelection(billId);

  const consumerDataId = billData?.consumerData?.id;
  const remoteConsumerNumber = billData?.consumerData?.consumerNo;
  const invoiceStartDate = billData?.invoiceStartDate;
  const invoiceEndDate = billData?.invoiceEndDate;

  const {
    data: consumerData,
    isLoading: isConsumerLoading,
    isError: isConsumerError,
  } = useConsumerDetails(consumerDataId);

  const {
    data: creditNoteData,
    isLoading: isCreditNoteLoading,
    isError: isCreditNoteError,
  } = useConsumerCreditNote(consumerDataId);

  const {
    data: latestBillData,
    isLoading: isLatestBillLoading,
    isError: isLatestBillError,
  } = useConsumerLatestBill(consumerDataId);

  const planId = latestBillData?.remotePlanId;

  const {
    data: meterReadingStatus,
    isLoading: isMeterStatusLoading,
    isError: isMeterStatusError,
  } = useMeterReadingStatus();

  const meterIds = consumerData?.meters?.map((m: any) => m) || [];

  const {
    data: meterData,
    isLoading: isMeterLoading,
    isError: isMeterError,
  } = useMeterdata(meterIds, invoiceStartDate, invoiceEndDate);
  const {
    data: planData,
    isLoading: isPlanLoading,
    isError: isPlanError,
  } = usePlanData(planId);

  // Load saved data on component mount
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const savedStepData = getStepData();
        if (savedStepData) {
          // Restore state from saved data
          setMeterUpdates(savedStepData.meterUpdates || {});
          setEnableOutstanding(savedStepData.enableOutstanding ?? false);
          setEnableLateFee(savedStepData.enableLateFee ?? false);
          setIncludeCredit(savedStepData.includeCredit ?? false);
        } else {
          // Initialize with data from step 2 if available
          if (step2Data) {
            setEnableOutstanding(step2Data.includeOutstandingFee ?? false);
            setEnableLateFee(step2Data.includeLateFee ?? false);
            setIncludeCredit(step2Data.includeCreditBalance ?? false);
          }
        }

        dataLoadedRef.current = true;
      } catch (error) {
        dataLoadedRef.current = true;
      }
    }
  }, [stepHelpers, currentStepIndex, getStepData, step2Data]);

  // Auto-save when state changes
  useEffect(() => {
    if (dataLoadedRef.current) {
      saveStepData({
        meterUpdates,
        enableOutstanding,
        enableLateFee,
        includeCredit,
        meterData: meterData
          ? meterData.map((meter) => ({
              id: meter.id,
              meterNumber: meter.meterNumber,
              deviceNo: meter.deviceNo,
              productCode: meter.productCode,
              previousReading: meter.previousReading,
              previousReadingDate: meter.previousReadingDate,
              previousReadingMeterStatusDisplay:
                meter.previousReadingMeterStatusDisplay,
              currentReading: meter.currentReading,
              currentReadingDate: meter.currentReadingDate,
              currentReadingMeterStatus: meter.currentReadingMeterStatus,
            }))
          : [],
        billData: billData
          ? {
              id: billData.id,
              consumerData: billData.consumerData,
              invoiceStartDate: billData.invoiceStartDate,
              invoiceEndDate: billData.invoiceEndDate,
              excludeOutstandingBalance: billData.excludeOutstandingBalance,
            }
          : null,
        lastUpdated: new Date().toISOString(),
      });
    }
  }, [
    meterUpdates,
    enableOutstanding,
    enableLateFee,
    includeCredit,
    meterData,
    billData,
  ]);

  const calculateConsumption = (current: string, previous: string): number => {
    const curr = Number(current);
    const prev = Number(previous);

    if (isNaN(curr) || isNaN(prev)) return 0;

    const result = curr - prev;
    return result >= 0 ? result : 0;
  };

  // Function to get current value for a meter field (either from updates or original data)
  const getMeterValue = (
    meterId: string,
    field: string,
    originalValue: any
  ) => {
    console.log(
      "Getting meter value:",
      meterId,
      field,
      originalValue,
      meterUpdates[meterId]?.[field]
    );
    return meterUpdates[meterId]?.[field] ?? originalValue;
  };

  // Function to update specific meter field
  const handleMeterChange = useCallback(
    (meterId: string, field: string, value: any) => {
      console.log("value", value);
      setMeterUpdates((prev) => ({
        ...prev,
        [meterId]: {
          ...prev[meterId],
          [field]: value,
        },
      }));
    },
    []
  );

  const handleDateChange = useCallback(
    (meterId: string, date: Date | undefined) => {
      if (date) {
        const formattedDate = format(date, "yyyy-MM-dd");
        handleMeterChange(meterId, "currentReadingDate", formattedDate);
      }
    },
    [handleMeterChange]
  );

  const formatDateToYYYYMMDD = (date: string | Date): Date => {
    if (date instanceof Date) return date;

    // If it's already in YYYY-MM-DD (safe format)
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return new Date(date);
    }

    // If it's in DD-MM-YYYY
    if (/^\d{2}-\d{2}-\d{4}$/.test(date)) {
      const [day, month, year] = date.split("-");
      return new Date(`${year}-${month}-${day}`);
    }

    // Invalid format fallback
    return new Date(NaN);
  };

  const parseFlexibleDateToString = (input: any) => {
    if (!input) return "";

    const knownFormats = [
      "YYYY-MM-DD",
      "DD MMM YYYY",
      "MMM DD YYYY",
      "DD/MM/YYYY",
      "MM/DD/YYYY",
      "YYYY/MM/DD",
      "DD-MM-YYYY",
      "YYYY-MM-DDTHH:mm:ss.SSSZ",
      "YYYY-MM-DDTHH:mm:ssZ",
      "ddd MMM DD YYYY HH:mm:ss [GMT]ZZ (z)",
    ];

    for (const format of knownFormats) {
      const date = dayjs(input, format, true);
      if (date.isValid()) return date.format("YYYY-MM-DD");
    }

    const fallback = dayjs(input); // try native parse
    return fallback.isValid() ? fallback.format("YYYY-MM-DD") : "";
  };

  // GenericTable columns configuration
  const meterColumns: TableColumn<MeterData>[] = [
    {
      key: "meterInfo",
      header: "Meter No/Device No",
      type: "custom",
      render: (_, row) => `${row.meterNumber}/${row.deviceNo}`,
    },
    {
      key: "productCode",
      header: "Utility",
      type: "text",
      render: (value) => value ?? "NA",
    },
    {
      key: "previousReading",
      header: "Last Reading",
      type: "text",
      render: (value) => value ?? "NA",
    },
    {
      key: "previousReadingMeterStatusDisplay",
      header: "Prev Status",
      type: "text",
      render: (value) => value ?? "NA",
    },
    {
      key: "previousReadingDate",
      header: "Last Date",
      type: "text",
      render: (value) => value ?? "NA",
    },
    {
      key: "currentReading",
      header: "Current Reading",
      type: "custom",
      render: (_, row) => {
        const currentReading = getMeterValue(
          row.id,
          "current_reading",
          row.currentReading
        );

        return (
          <input
            type="number"
            className="border rounded px-2 py-0.5 w-20 text-sm"
            value={currentReading ?? ""}
            onChange={(e) =>
              handleMeterChange(
                row.id,
                "current_reading",
                Number(e.target.value)
              )
            }
            placeholder="Enter reading"
          />
        );
      },
    },
    {
      key: "currentReadingMeterStatus",
      header: "Curr Status",
      type: "custom",
      render: (_, row) => {
        const currentStatus = getMeterValue(
          row.id,
          "current_reading_meter_status",
          row.currentReadingMeterStatus
        );

        return (
          <select
            className="border rounded px-1 py-0 text-xs w-24"
            value={currentStatus ?? ""}
            onChange={(e) =>
              handleMeterChange(
                row.id,
                "current_reading_meter_status",
                e.target.value
              )
            }
          >
            <option value="">Select Status</option>
            {meterReadingStatus?.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.value}
              </option>
            ))}
          </select>
        );
      },
    },
    {
      key: "currentReadingDate",
      header: "Current Date",
      type: "custom",
      render: (_, row) => {
        const currentDate = getMeterValue(
          row.id,
          "currentReadingDate",
          row.currentReadingDate || new Date().toISOString()
        );

        return (
          <input
            type="date"
            className="border rounded px-2 py-1 text-xs w-32"
            value={
              currentDate != "N/A" ? parseFlexibleDateToString(currentDate) : ""
            }
            onChange={(e) => {
              const selectedDate = e.target.value
                ? new Date(e.target.value)
                : undefined;
              handleDateChange(row.id, selectedDate);
            }}
          />
        );
      },
    },
    {
      key: "consumption",
      header: "Consumption",
      type: "custom",
      render: (_, row) => {
        const currentReading = getMeterValue(
          row.id,
          "current_reading",
          row.currentReading
        );

        const consumption = calculateConsumption(
          currentReading?.toString() || "0",
          row.previousReading?.toString() || "0"
        );

        return (
          <div
            className={cn(
              "font-medium",
              consumption < 0 ? "text-red-600" : "text-green-600"
            )}
          >
            {consumption} units
          </div>
        );
      },
    },
  ];

  const handleContinue = async () => {
    // Clear any previous validation errors
    if (clearValidationError) {
      clearValidationError();
    }

    // Validate meter readings
    if (!meterData || meterData.length === 0) {
      if (setValidationError) {
        setValidationError(
          "No meter data found. Please check the consumer setup."
        );
      }
      return;
    }

    // Validate each meter reading
    for (let i = 0; i < meterData.length; i++) {
      const meter = meterData[i];
      const prev = Number(meter.previousReading || 0);
      const curr = Number(
        getMeterValue(meter.id, "current_reading", meter.currentReading) || 0
      );

      // Check current reading >= previous reading
      if (curr < prev) {
        if (setValidationError) {
          setValidationError(
            `Current reading for meter ${meter.meterNumber} cannot be less than previous reading (${prev})`
          );
        }
        return;
      }

      const currentDate = getMeterValue(
        meter.id,
        "currentReadingDate",
        meter.currentReadingDate || new Date()
      );
      const currentDateStr = formatDateToYYYYMMDD(currentDate);

      if (
        currentDateStr < formatDateToYYYYMMDD(invoiceStartDate) ||
        currentDateStr > formatDateToYYYYMMDD(invoiceEndDate)
      ) {
        if (setValidationError) {
          setValidationError(
            `Reading date for meter ${meter.meterNumber} must be between ${invoiceStartDate} and ${invoiceEndDate}`
          );
        }
        return;
      }
    }

    try {
      const reading_data = meterData
        .map((meter) => {
          const currentReading = getMeterValue(
            meter.id,
            "current_reading",
            meter.currentReading
          );
          const currentDate = getMeterValue(
            meter.id,
            "currentReadingDate",
            meter.currentReadingDate || new Date().toISOString()
          );
       
          const currentStatus =
            getMeterValue(
              meter.id,
              "current_reading_meter_status",
              meter.current_reading_meter_status
            ) ?? 0;

          return {
            meter: meter.id,
            meter_number: meter.meterNumber,
            product_code: meter.productCode,
            meter_reading: currentReading,
            reading_source: 1,
            reading_date: parseFlexibleDateToString(currentDate),
            status: currentStatus,
          };
        })
        .filter((entry) => entry !== null);

      const payload = {
        bill_generate: true,
        bill_data: {
          exclude_outstanding_balance: !enableOutstanding,
          include_credit: includeCredit,
        },
        reading_data,
        step: {
          step_working_on: 2,
          total_steps: 3,
        },
      };

      const response = await updateConsumer(payload);

      if (response?.billId) {
        // Save final step data with completion status and the new billId
        saveStepData({
          meterUpdates,
          enableOutstanding,
          enableLateFee,
          includeCredit,
          payload,
          response,
          reading_data,
          finalBillId: response.billId, // This is the generated bill ID for step 4
          meterReadings: meterData.map((meter) => ({
            id: meter.id,
            meterNumber: meter.meterNumber,
            productCode: meter.productCode,
            previousReading: meter.previousReading,
            currentReading: getMeterValue(
              meter.id,
              "current_reading",
              meter.currentReading
            ),
            consumption: calculateConsumption(
              getMeterValue(
                meter.id,
                "current_reading",
                meter.currentReading
              )?.toString() || "0",
              meter.previousReading?.toString() || "0"
            ),
            currentReadingDate: getMeterValue(
              meter.id,
              "currentReadingDate",
              meter.currentReadingDate
            ),
            currentStatus: getMeterValue(
              meter.id,
              "current_reading_meter_status",
              meter.current_reading_meter_status
            ),
          })),
          paymentSummary: {
            lastBillDate: latestBillData?.billData?.createdDate,
            lastBillAmount: latestBillData?.billData?.totalAmountPayable,
            lastPaymentDate: latestBillData?.billData?.lastPaymentDate,
            lastPaymentAmount: latestBillData?.billData?.lastPayAmount,
            outstandingBalance: latestBillData?.billData?.outstandingBalance,
            availableCredit: creditNoteData?.credit,
            enableOutstanding,
            enableLateFee,
            includeCredit,
          },
          isStepComplete: true,
          completedAt: new Date().toISOString(),
        });

        // Proceed to next step
        if (onNext) {
          onNext();
        }
      } else {
        if (setValidationError) {
          setValidationError("Failed to generate bill. Please try again.");
        }
      }
    } catch (error) {
      console.error("Failed to continue from step 3:", error);
      if (setValidationError) {
        setValidationError(
          "An error occurred while processing readings. Please try again."
        );
      }
    }
  };

  const handlePrevious = () => {
    // Save current state before going back
    saveStepData({
      meterUpdates,
      enableOutstanding,
      enableLateFee,
      includeCredit,
      navigatedBack: true,
      navigatedBackAt: new Date().toISOString(),
    });

    if (onPrevious) {
      onPrevious();
    }
  };

  // Handle toggle changes with auto-save
  const handleEnableOutstandingChange = useCallback((checked: boolean) => {
    setEnableOutstanding(checked);
  }, []);

  const handleEnableLateFeeChange = useCallback((checked: boolean) => {
    setEnableLateFee(checked);
  }, []);

  const handleIncludeCreditChange = useCallback((checked: boolean) => {
    setIncludeCredit(checked);
  }, []);

  // Show loading state if critical data is not available
  if (isBillLoading || !billId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading meter and payment data...</p>
        </div>
      </div>
    );
  }

  // Show error state if critical data failed to load
  if (isBillError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load meter data.</p>
          <Button onClick={handlePrevious} variant="outline">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="card-subheading-text">
          Enter current meter readings and configure payment settings for bill
          generation.
        </p>
      </div>

      <div className="bg-white rounded-lg border shadow-sm">
        <div className="px-6 py-2">
          <h3 className="card-heading-text">Meter Readings & Consumption</h3>
        </div>

        <div className="p-4 pt-2">
          <GenericTable
            data={meterData || []}
            columns={meterColumns}
            loading={isMeterLoading}
            rowKey="id"
            emptyMessage="No meter data available"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg border shadow-sm">
        <div className="p-2 px-4">
          <h3 className="card-heading-text">Payment Summary & Credit</h3>
        </div>

        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex flex-col">
                <Label
                  htmlFor="lastBillDate"
                  className="mb-1 text-muted-foreground text-sm"
                >
                  Last Bill Date
                </Label>
                <div className="font-medium">
                  {latestBillData?.billData?.createdDate ?? "NA"}
                </div>
              </div>

              <div className="flex flex-col">
                <Label
                  htmlFor="lastBillAmount"
                  className="mb-1 text-muted-foreground text-sm"
                >
                  Last Bill Amount
                </Label>
                <div className="font-medium">
                  {CURRENCY}{" "}
                  {latestBillData?.billData?.totalAmountPayable ?? "NA"}
                </div>
              </div>

              <div className="flex flex-col">
                <Label
                  htmlFor="lastPaymentDate"
                  className="mb-1 text-muted-foreground text-sm"
                >
                  Last Payment Date
                </Label>
                <div className="font-medium">
                  {latestBillData?.billData?.lastPaymentDate ?? "NA"}
                </div>
              </div>

              <div className="flex flex-col">
                <Label
                  htmlFor="lastPaymentAmount"
                  className="mb-1 text-muted-foreground text-sm"
                >
                  Last Payment Amount
                </Label>
                <div className="font-medium">
                  {CURRENCY} {latestBillData?.billData?.lastPayAmount ?? "NA"}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label htmlFor="outstanding">
                    Outstanding Amount ({CURRENCY})
                  </Label>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="enableOutstanding" className="text-sm">
                      Enable
                    </Label>
                    <Switch
                      id="enableOutstanding"
                      checked={enableOutstanding}
                      onCheckedChange={handleEnableOutstandingChange}
                    />
                  </div>
                </div>
                <Input
                  id="outstanding"
                  type="number"
                  disabled={true}
                  value={latestBillData?.billData?.outstandingBalance ?? "0"}
                />
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label htmlFor="lateFee">Late Fee ({CURRENCY})</Label>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="enableLateFee" className="text-sm">
                      Enable
                    </Label>
                    <Switch
                      id="enableLateFee"
                      checked={enableLateFee}
                      onCheckedChange={handleEnableLateFeeChange}
                    />
                  </div>
                </div>
                <Input
                  id="lateFee"
                  type="number"
                  disabled={true}
                  value={
                    planData?.utilityServiceData?.utilityServiceCharges?.[
                      "Late Payment"
                    ]?.fee ?? "0"
                  }
                />
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label htmlFor="availableCredit">
                    Available Credit ({CURRENCY})
                  </Label>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="includeCredit" className="text-sm">
                      Include
                    </Label>
                    <Switch
                      id="includeCredit"
                      checked={includeCredit}
                      onCheckedChange={handleIncludeCreditChange}
                    />
                  </div>
                </div>
                <Input
                  id="availableCredit"
                  type="number"
                  disabled={true}
                  value={creditNoteData?.credit ?? "0"}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-6">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={isSubmitting || isValidating}
        >
          Previous
        </Button>
        <Button
          onClick={handleContinue}
          disabled={isSubmitting || isValidating}
          className="min-w-[200px]"
        >
          {isSubmitting || isValidating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {isSubmitting ? "Generating Bill..." : "Validating..."}
            </>
          ) : (
            "Generate Bill & Continue"
          )}
        </Button>
      </div>
    </div>
  );
};

export default Step3ReadingsConsumption;